import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Observable, of, throwError, from } from "rxjs";
import { map, catchError, timeout, switchMap } from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TimeoutError } from "rxjs";
import { AppConfigService } from "src/services/appconfig.service";
import { UserMetricResponse } from "../components/user-metrics/user-metrics.component";
import { GraphService } from "src/services/graph.service";

@Injectable({
  providedIn: "root",
})
export class AdminService {
  private apiurl: string = this.appConfig.getConfig("apiUrl");
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private appConfig: AppConfigService,
    private graphService: GraphService
  ) { }

  // Create headers with authentication token
  private async createAuthHeaders(): Promise<HttpHeaders> {
    const token = await this.graphService.getApiAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  }

  getAllMembers(data: Record<string, unknown>): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<unknown>(this.apiurl + "/get-all-members", data, { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in getAllMembers:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }

  getEmail(data: Record<string, unknown>): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<unknown>(this.apiurl + "/get-user-email", data, { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in getEmail:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }

  accessOperations(data: Record<string, unknown>): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<unknown>(this.apiurl + "/access-control", data, { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in accessOperations:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }
  // /store-access-request
  getRequestQueue(): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<unknown>(this.apiurl + "/get-access-requests", { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in get-access-requests:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }
  accessPermission(data: Record<string, unknown>): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<unknown>(this.apiurl + "/store-access-request", data, { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in accessPermission:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }

  userMetricData(): Observable<UserMetricResponse> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UserMetricResponse>(this.apiurl + "/get_user_usage_logs", { headers }).pipe(
          timeout(12000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in userMetricData:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }
  private errorHandler(error: HttpErrorResponse | TimeoutError) {
    if (error instanceof TimeoutError) {
      this.snackBar.open(
        "This is taking longer than usual. Please refresh your browser",
        "Close",
        { duration: 3000 }
      );
    } else {
      this.snackBar.open("An error occurred. Please try again.", "Close", {
        duration: 3000,
      });
    }
    return throwError(() => new Error(error.message));
  }

  getAccessRoles(data: Record<string, unknown>): Observable<unknown> {
    return from(this.createAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<unknown>(this.apiurl + "/define-persona", data, { headers }).pipe(
          timeout(120000),
          map((response) => response),
          catchError((err) => {
            console.error("Error in getAccessRoles:", err);
            return this.errorHandler(err) || of([]);
          })
        );
      })
    );
  }


}
